import {
  Cell,
  CellType,
  createCell,
  createNote,
  createNotebook,
  FletchyVault,
  Note,
  normalizeVault,
} from "@fletchy/core";

export type ViewMode = "edit" | "split" | "preview";
export type ThemeMode = "system" | "light" | "dark";
export type CellMoveDirection = "up" | "down";

export interface VaultCreationResult {
  vault: FletchyVault;
  notebookId: string | null;
  noteId: string | null;
}

export interface NotebookCreationResult extends VaultCreationResult {
  notebookTitle: string;
}

const DEFAULT_MERMAID_CONTENT = "flowchart LR\n  Idea --> Note\n  Note --> Export";

function nowIso() {
  return new Date().toISOString();
}

function findNote(vault: FletchyVault, noteId: string) {
  return vault.notes.find((note) => note.id === noteId) ?? null;
}

export function viewModeFor(vault: FletchyVault): ViewMode {
  return vault.settings.splitPreview ? "split" : "edit";
}

export function firstActiveNoteId(vault: FletchyVault): string | null {
  return vault.notes.find((note) => !note.trashedAt)?.id ?? null;
}

export function mergeVaults(current: FletchyVault, imported: FletchyVault): FletchyVault {
  return normalizeVault({
    ...current,
    notebooks: [...current.notebooks, ...imported.notebooks],
    notes: [...current.notes, ...imported.notes],
    cells: [...current.cells, ...imported.cells],
  });
}

export function createNotebookWithInitialNote(current: FletchyVault): NotebookCreationResult {
  const notebook = createNotebook("New Notebook", current.notebooks.length);
  const note = createNote(notebook.id, "Untitled Note", 0);
  const cell = createCell(note.id, "markdown", "", 0);

  return {
    vault: {
      ...current,
      notebooks: [...current.notebooks, notebook],
      notes: [...current.notes, { ...note, cellIds: [cell.id] }],
      cells: [...current.cells, cell],
    },
    notebookId: notebook.id,
    noteId: note.id,
    notebookTitle: notebook.title,
  };
}

export function createNoteInNotebook(current: FletchyVault, notebookId: string): VaultCreationResult {
  const notebook = current.notebooks.find((item) => item.id === notebookId);

  if (!notebook) {
    return {
      vault: current,
      notebookId: null,
      noteId: null,
    };
  }

  const notebookNotes = current.notes.filter((note) => note.notebookId === notebook.id);
  const note = createNote(notebook.id, "Untitled Note", notebookNotes.length);
  const cell = createCell(note.id, "markdown", "", 0);

  return {
    vault: {
      ...current,
      notes: [...current.notes, { ...note, cellIds: [cell.id] }],
      cells: [...current.cells, cell],
    },
    notebookId: notebook.id,
    noteId: note.id,
  };
}

export function updateNote(current: FletchyVault, noteId: string, patch: Partial<Note>, updatedAt = nowIso()): FletchyVault {
  return {
    ...current,
    notes: current.notes.map((note) => (note.id === noteId ? { ...note, ...patch, updatedAt } : note)),
  };
}

export function addCellToNote(current: FletchyVault, noteId: string, type: CellType): VaultCreationResult {
  const note = findNote(current, noteId);

  if (!note) {
    return {
      vault: current,
      notebookId: null,
      noteId: null,
    };
  }

  const noteCells = current.cells.filter((cell) => note.cellIds.includes(cell.id));
  const cell = createCell(
    note.id,
    type,
    type === "mermaid" ? DEFAULT_MERMAID_CONTENT : "",
    noteCells.length,
    type === "code" ? { language: "typescript" } : {},
  );
  const updatedAt = nowIso();

  return {
    vault: {
      ...current,
      notes: current.notes.map((item) =>
        item.id === note.id ? { ...item, cellIds: [...item.cellIds, cell.id], updatedAt } : item,
      ),
      cells: [...current.cells, cell],
    },
    notebookId: note.notebookId,
    noteId: note.id,
  };
}

export function updateCellInNote(current: FletchyVault, noteId: string, updatedCell: Cell, updatedAt = nowIso()): FletchyVault {
  return {
    ...current,
    notes: current.notes.map((note) => (note.id === noteId ? { ...note, updatedAt } : note)),
    cells: current.cells.map((cell) => (cell.id === updatedCell.id ? updatedCell : cell)),
  };
}

export function deleteCellFromNote(current: FletchyVault, noteId: string, cellId: string, updatedAt = nowIso()): FletchyVault {
  const note = findNote(current, noteId);

  if (!note || note.cellIds.length <= 1) {
    return current;
  }

  return {
    ...current,
    notes: current.notes.map((item) =>
      item.id === note.id ? { ...item, cellIds: item.cellIds.filter((id) => id !== cellId), updatedAt } : item,
    ),
    cells: current.cells.filter((cell) => cell.id !== cellId),
  };
}

export function moveCellInNote(
  current: FletchyVault,
  noteId: string,
  cellId: string,
  direction: CellMoveDirection,
  updatedAt = nowIso(),
): FletchyVault {
  const note = findNote(current, noteId);

  if (!note) {
    return current;
  }

  const index = note.cellIds.indexOf(cellId);
  const targetIndex = direction === "up" ? index - 1 : index + 1;

  if (index < 0 || targetIndex < 0 || targetIndex >= note.cellIds.length) {
    return current;
  }

  const nextIds = [...note.cellIds];
  [nextIds[index], nextIds[targetIndex]] = [nextIds[targetIndex], nextIds[index]];

  return {
    ...current,
    notes: current.notes.map((item) => (item.id === note.id ? { ...item, cellIds: nextIds, updatedAt } : item)),
    cells: current.cells.map((cell) => {
      const sortOrder = nextIds.indexOf(cell.id);
      return sortOrder >= 0 ? { ...cell, sortOrder } : cell;
    }),
  };
}

export function trashNote(current: FletchyVault, noteId: string, trashedAt = nowIso()): FletchyVault {
  return {
    ...current,
    notes: current.notes.map((note) => (note.id === noteId ? { ...note, trashedAt } : note)),
  };
}

export function renameNotebook(current: FletchyVault, notebookId: string, title: string, updatedAt = nowIso()): FletchyVault {
  const nextTitle = title.trim() || "New Notebook";

  return {
    ...current,
    notebooks: current.notebooks.map((notebook) =>
      notebook.id === notebookId ? { ...notebook, title: nextTitle, updatedAt } : notebook,
    ),
  };
}

export function setVaultTheme(current: FletchyVault, theme: ThemeMode): FletchyVault {
  return {
    ...current,
    settings: {
      ...current.settings,
      theme,
    },
  };
}
